// The DM's "Rewind…" (issue 1169): the scene's recent moments — each the scene just before a change, with its time,
// who made it and the history's sentence — and "5 minutes ago" as a quick choice; then a confirm that says what comes
// back and that rolls stay. The rewind is one act over the socket; the server works out and stamps what it restores.
import { useEffect, useState } from 'react';
import { Modal } from './Dialogs';
import { fetchMoments, momentDay, momentTime, momentWhen, type Moment } from '../rewind';
import { BTN_MD, PRIMARY } from './ui';

const btn = BTN_MD;
const primary = PRIMARY;

/** What a rewind brings back, and what it leaves: the confirm's words. */
export const REWIND_RESTORES = 'Every token where it stood, with its hit points and conditions; the fog, walls, lights and drawings; the turn order and its effects.';
export const REWIND_KEEPS = 'Rolls made since stay in the log, marked as made before the rewind.';

/** The chosen moment: one of the list, or "five minutes ago" by the server's clock. */
type Choice = { kind: 'moment'; moment: Moment } | { kind: 'ago'; seconds: number };

/** Who made a moment's change, as the dialog says it: the table's name for them, else their handle, else nobody. */
const byHandle = (m: Moment): string | null => (m.who ? `@${m.who}` : null);

export function RewindDialog({ campaignId, sceneId, onRewind, onClose, nameOf = byHandle, fetchImpl = fetch }: {
  campaignId: string; sceneId: string | null;
  /** Names who made a moment's change as the table names them (issue 1023); null leaves the sentence without one. */
  nameOf?: (m: Moment) => string | null;
  /** Send the rewind: `{ to }` for a moment, `{ ago }` for the quick choice. */
  onRewind: (body: { kind: 'scene.rewind'; to: string } | { kind: 'scene.rewind'; ago: number }) => void;
  onClose: () => void;
  fetchImpl?: typeof fetch;
}) {
  const [moments, setMoments] = useState<Moment[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [choice, setChoice] = useState<Choice | null>(null);
  useEffect(() => {
    let live = true;
    void fetchMoments(campaignId, sceneId, fetchImpl).then((m) => { if (!live) return; if (m) setMoments(m.moments); else setFailed(true); });
    return () => { live = false; };
  }, [campaignId, sceneId, fetchImpl]);

  const said = (m: Moment): string => { const who = nameOf(m); return who ? `${who} ${m.summary}` : m.summary; };

  if (choice) {
    const day = choice.kind === 'moment' ? momentDay(choice.moment.at) : null;
    const when = choice.kind === 'ago' ? 'five minutes ago' : `${day ? `${day}, ` : ''}${momentTime(choice.moment.at)}, just before ${said(choice.moment)}`;
    return (
      <Modal title="Rewind the table?" onClose={onClose} testId="rewind-confirm">
        <div className="space-y-3 text-sm text-stone-300">
          <p>Back to {when}, for everyone on this scene:</p>
          <p>{REWIND_RESTORES}</p>
          <p className="text-stone-400">{REWIND_KEEPS}</p>
          <div className="flex justify-end gap-2">
            <button type="button" className={btn} onClick={() => setChoice(null)}>Back</button>
            <button type="button" className={primary} data-testid="rewind-go"
              onClick={() => { onRewind(choice.kind === 'ago' ? { kind: 'scene.rewind', ago: choice.seconds } : { kind: 'scene.rewind', to: choice.moment.to }); onClose(); }}>Rewind</button>
          </div>
        </div>
      </Modal>
    );
  }
  return (
    <Modal title="Rewind the table" onClose={onClose} testId="rewind-dialog">
      <p className="mb-3 text-sm text-stone-300">Choose a moment to go back to. The board and the turn order return to how they were; rolls stay in the log.</p>
      <button type="button" className={`${btn} mb-3`} data-testid="rewind-five" onClick={() => setChoice({ kind: 'ago', seconds: 300 })}>5 minutes ago</button>
      {failed && <p className="text-sm text-red-300">The table's moments could not be read just now.</p>}
      {!failed && moments === null && <p className="text-sm text-stone-500">Reading the scene's history…</p>}
      {moments?.length === 0 && <p className="text-sm text-stone-500">Nothing has changed on this scene yet.</p>}
      {moments && moments.length > 0 && (
        <ol className="max-h-72 space-y-0.5 overflow-auto" data-testid="rewind-moments">
          {moments.map((m) => (
            <li key={m.to}>
              <button type="button" className="w-full rounded px-2 py-1 text-left text-xs hover:bg-stone-800" data-testid="rewind-moment" onClick={() => setChoice({ kind: 'moment', moment: m })}>
                <span className="font-mono text-stone-400">{momentWhen(m.at)}</span> Before {said(m)}
              </button>
            </li>
          ))}
        </ol>
      )}
      <div className="mt-4 flex justify-end"><button type="button" className={btn} onClick={onClose}>Cancel</button></div>
    </Modal>
  );
}
